import type {
  FeedbackAdminItem,
  FeedbackAdminItemDto,
  FeedbackAdminListResponseDto,
  FeedbackAttachment,
  FeedbackAttachmentDto,
  FeedbackListResponseDto,
  FeedbackPage,
  FeedbackReportDetail,
  FeedbackReportDetailDto,
  FeedbackReportItem,
  FeedbackReportItemDto,
  FeedbackResolutionTag,
  FeedbackResolutionTagDto,
  FeedbackResolutionTagsResponseDto,
  FeedbackSummary,
  FeedbackSummaryDto,
} from './feedback';

/**
 * 問題回報讀取端 wire DTO(snake_case)→ domain mapper(R265;BE docs/94 §5 / §9)。
 * -----------------------------------------------------------------------------
 * ## 為什麼整支 fail-soft
 *
 * BE 這批是**漸進部署**的:`resolution_note_public` / `resolution_tags` /
 * `status_updated_datetime` 是 2026-08-19 才加到 list item 上的三欄,dev 上跑的版本
 * 可能還沒有它們。缺欄一律降級成「沒有處理結論」(`null` / `[]`)而不是炸掉或顯示
 * 空白 —— **同一條渲染路徑**吃兩種形狀,BE 部署當天 FE 零改動就亮起來。
 *
 * ## 三條刻意的判準
 *
 * 1. **`status` 原樣保留**(不做 union 收斂)—— 未知值由 `feedbackPresent.statusMeta`
 *    顯 wire 原字串 + 中性色。拿 FE union 過濾 wire 值域是 R228 紅線。
 * 2. **附件只收有 `download_url` 的**:那個 URL 是取圖的唯一憑證,沒有它的 item
 *    渲染出來就是一個永遠壞掉的縮圖。
 * 3. **`internal_note` 只在 admin mapper 出現** —— user 視角的 DTO 根本沒有這個欄位
 *    (BE 也不會回),型別上就走不到,不需要 runtime 判斷。
 */

// ─── 小工具 ──────────────────────────────────────────────────────────────────

/** 非空字串 → 原值;其餘(null / undefined / 空字串 / 非字串)→ null。 */
function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/** 有限數 → 原值;其餘 → fallback(BQ / JSON 有時把數字送成字串,一併吸收)。 */
function num(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

// ─── 標籤 ────────────────────────────────────────────────────────────────────

/**
 * `{code,label}` → domain。**label 缺席時退回 code**(顯示得出東西比顯示空白好;
 * code 至少讓 admin 認得出是哪個標籤)。code 缺席 → 丟棄整筆(沒有 code 的標籤
 * 對誰都沒有意義)。
 */
export function mapResolutionTagDto(dto: FeedbackResolutionTagDto | null | undefined): FeedbackResolutionTag | null {
  const code = str(dto?.code);
  if (!code) return null;
  return { code, label: str(dto?.label) ?? code };
}

export function mapResolutionTagsDto(
  list: FeedbackResolutionTagDto[] | null | undefined,
): FeedbackResolutionTag[] {
  if (!Array.isArray(list)) return [];
  return list.map(mapResolutionTagDto).filter((t): t is FeedbackResolutionTag => t !== null);
}

/** `GET /feedback/resolution-tags` 的回應 → 目錄(admin 選單用)。 */
export function mapResolutionTagsResponseDto(
  dto: FeedbackResolutionTagsResponseDto | null | undefined,
): FeedbackResolutionTag[] {
  return mapResolutionTagsDto(dto?.items);
}

// ─── 附件 ────────────────────────────────────────────────────────────────────

export function mapAttachmentsDto(
  list: FeedbackAttachmentDto[] | null | undefined,
): FeedbackAttachment[] {
  if (!Array.isArray(list)) return [];
  const out: FeedbackAttachment[] = [];
  for (const a of list) {
    const url = str(a?.download_url);
    // 沒有 signed URL 的附件渲染出來就是一個永遠壞掉的縮圖 —— 不如當它不存在
    // (`attachment_count` 仍然會誠實顯示「有幾張」)。
    if (!url) continue;
    out.push({
      index: num(a?.index, out.length + 1),
      width: num(a?.width, 0),
      height: num(a?.height, 0),
      sizeBytes: num(a?.size_bytes, 0),
      downloadUrl: url,
      expiresAt: str(a?.download_url_expires_datetime),
    });
  }
  return out;
}

// ─── item / detail / admin ───────────────────────────────────────────────────

export function mapFeedbackItemDto(dto: FeedbackReportItemDto): FeedbackReportItem {
  return {
    reportId: str(dto?.report_id) ?? '',
    serial: str(dto?.serial) ?? '',
    category: str(dto?.category) ?? 'other',
    title: str(dto?.title) ?? '',
    // 空字串是合法的(BE 對「有截圖或診斷」的回報允許空 description)。
    description: typeof dto?.description === 'string' ? dto.description : '',
    // ⚠️ 原樣保留 —— 顯示 gate 在 feedbackPresent,不在這裡收斂(檔頭判準 1)。
    status: str(dto?.status) ?? '',
    attachmentCount: num(dto?.attachment_count, 0),
    createdAt: str(dto?.created_datetime),
    updatedAt: str(dto?.updated_datetime),
    // ↓ 這三欄是 2026-08-19 才加到 list item 上的;BE 未部署那版沒有它們 → 缺席
    //   一律降級成「沒有處理結論」,而不是顯示空白框或炸掉。
    resolutionNotePublic: str(dto?.resolution_note_public),
    resolutionTags: mapResolutionTagsDto(dto?.resolution_tags),
    statusUpdatedAt: str(dto?.status_updated_datetime),
  };
}

export function mapFeedbackDetailDto(dto: FeedbackReportDetailDto): FeedbackReportDetail {
  return {
    ...mapFeedbackItemDto(dto),
    pageUrl: str(dto?.page_url),
    appVersion: str(dto?.app_version),
    viewport: str(dto?.viewport),
    occurredAt: str(dto?.occurred_at),
    projectId: str(dto?.project_id),
    jobId: str(dto?.job_id),
    nodeId: str(dto?.node_id),
    attachments: mapAttachmentsDto(dto?.attachments),
    attachmentFailedCount: num(dto?.attachment_failed_count, 0),
  };
}

/**
 * admin 視角。⚠️ **`attachments` 在清單那條會缺席**(router `exclude` 掉,見
 * `types/feedback.ts` 檔頭)→ 這裡會得到 `[]`;admin 要看圖得另打
 * `GET /feedback/reports/{id}`。這不是漏接,是 BE 為了省 60 次 SignBlob 的設計。
 */
export function mapFeedbackAdminItemDto(dto: FeedbackAdminItemDto): FeedbackAdminItem {
  return {
    ...mapFeedbackDetailDto(dto),
    reporterUid: str(dto?.reporter_uid),
    reporterEmail: str(dto?.reporter_email),
    reporterDisplayName: str(dto?.reporter_display_name),
    groupId: str(dto?.group_id),
    userAgent: str(dto?.user_agent),
    internalNote: str(dto?.internal_note),
    logfireTraceId: str(dto?.logfire_trace_id),
    clientReportId: str(dto?.client_report_id),
    route: str(dto?.route),
    diagnosticsJson: str(dto?.diagnostics_json),
    statusUpdatedByUid: str(dto?.status_updated_by_uid),
  };
}

// ─── 清單 / 計數 ─────────────────────────────────────────────────────────────

export function mapFeedbackListDto(
  dto: FeedbackListResponseDto | null | undefined,
): FeedbackPage<FeedbackReportItem> {
  return {
    items: Array.isArray(dto?.items) ? dto.items.map(mapFeedbackItemDto) : [],
    // ⚠️ cursor **原封不動**(opaque base64;解析它就是在賭 BE 的內部格式)。
    nextCursor: str(dto?.next_cursor),
  };
}

export function mapFeedbackAdminListDto(
  dto: FeedbackAdminListResponseDto | null | undefined,
): FeedbackPage<FeedbackAdminItem> {
  return {
    items: Array.isArray(dto?.items) ? dto.items.map(mapFeedbackAdminItemDto) : [],
    nextCursor: str(dto?.next_cursor),
  };
}

/**
 * 狀態計數。`counts` 只收數字值 —— 未知狀態的 key 也照收(BE 加狀態時 chips 那側
 * 由契約鎖負責紅,但這裡不該把它吞掉)。
 *
 * ⚠️ **`openTotal` 直接用 BE 的值,不自己加** —— docs/94 §5 明說「日後多一個狀態時
 * 後端會一起改」。自己加 = 多一個狀態那天靜默算錯。
 */
export function mapFeedbackSummaryDto(
  dto: FeedbackSummaryDto | null | undefined,
): FeedbackSummary {
  const counts: Record<string, number> = {};
  const raw = dto?.counts;
  if (raw && typeof raw === 'object') {
    for (const [k, v] of Object.entries(raw)) {
      const n = num(v, Number.NaN);
      if (Number.isFinite(n)) counts[k] = n;
    }
  }
  return {
    counts,
    total: num(dto?.total, 0),
    openTotal: num(dto?.open_total, 0),
  };
}
